import { Send, Users } from 'lucide-react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { useState } from 'react';

import { AnimatedCount } from '@/shared/ui/animated-count';
import { useCohortRetention, useCohortSummary } from '@/features/cohort';
import { CouponIssueFromAnalysisDialog, useCohortNonRepurchaseMembers } from '@/features/coupon';
import { useSyncStatus } from '@/features/sync';
import { AnalysisGuide } from '@/shared/ui/analysis-guide';
import { AnimatedContainer } from '@/shared/ui/animated';
import { MemberOnlyNotice } from '@/shared/ui/member-only-notice';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { InfoTooltip } from '@/shared/ui/info-tooltip';
import { DataErrorState } from '@/shared/ui/data-error-state';
import { EmptyState } from '@/shared/ui/empty-state';
import { NeedsSyncState } from '@/shared/ui/needs-sync-state';
import { PageHeader } from '@/shared/ui/page-header';
import { testIds } from '@/shared/testing';
import { Skeleton } from '@/shared/ui/skeleton';
import { AppLayout } from '../../-components/app-layout';

import type { CohortType } from '@cafe24-apps/traffic-light-server/contracts';

export const Route = createFileRoute('/_authenticated/$mallId/analysis/cohort/')({
  component: CohortPage,
});

const COHORT_TYPE_LABELS: Record<CohortType, string> = {
  first_purchase_month: '첫구매 시점',
  channel: '마켓 채널',
  amount_range: '금액대',
  segment: 'RFM 세그먼트',
};

const COHORT_TYPE_DESCRIPTIONS: Record<CohortType, string> = {
  first_purchase_month:
    '같은 달에 처음 구매한 고객끼리 묶어서, 시간이 지나도 다시 사는지 비교해요.',
  channel: '어떤 경로로 들어온 고객이 오래 남는지 채널별로 비교해요.',
  amount_range: '첫 구매 금액이 큰 고객이 더 자주 다시 사는지 확인해요.',
  segment: '현재 활성/주의/이탈 고객이 과거에 어떤 흐름이었는지 되돌아봐요.',
};

const COHORT_TYPES: CohortType[] = ['first_purchase_month', 'channel', 'amount_range', 'segment'];

function getHeatmapColor(rate: number): string {
  if (rate >= 0.5) return 'bg-primary text-primary-foreground';
  if (rate >= 0.4) return 'bg-primary/80 text-primary-foreground';
  if (rate >= 0.3) return 'bg-primary/60 text-primary-foreground';
  if (rate >= 0.2) return 'bg-primary/40 text-foreground';
  if (rate >= 0.1) return 'bg-primary/20 text-foreground';
  if (rate > 0) return 'bg-primary-bg text-foreground';
  return 'bg-muted text-muted-foreground';
}

function SummaryCards({
  avgRetentionRate,
  totalCustomers,
  calculatedAt,
  isLoading,
}: {
  avgRetentionRate: number;
  totalCustomers: number;
  calculatedAt: string | null;
  isLoading: boolean;
}) {
  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-3 gap-4"
      data-testid={testIds.cohort.summaryCards}
    >
      <Card className="py-4" data-testid={testIds.cohort.avgRetention}>
        <CardContent className="flex flex-col gap-1">
          <p className="text-xs text-muted-foreground inline-flex items-center">
            평균 재구매율
            <InfoTooltip content="모든 고객 그룹의 월별 재구매율을 평균 낸 값이에요. 높을수록 고객이 꾸준히 다시 사고 있다는 뜻이에요." />
          </p>
          {isLoading ? (
            <Skeleton className="h-7 w-20" />
          ) : (
            <p className="text-2xl font-semibold">
              <AnimatedCount value={avgRetentionRate * 100} decimals={1} />%
            </p>
          )}
        </CardContent>
      </Card>
      <Card className="py-4" data-testid={testIds.cohort.totalCustomers}>
        <CardContent className="flex flex-col gap-1">
          <p className="text-xs text-muted-foreground">총 회원 수</p>
          {isLoading ? (
            <Skeleton className="h-7 w-20" />
          ) : (
            <p className="text-2xl font-semibold">
              <AnimatedCount value={totalCustomers} />명
            </p>
          )}
        </CardContent>
      </Card>
      <Card className="py-4" data-testid={testIds.cohort.lastCalculated}>
        <CardContent className="flex flex-col gap-1">
          <p className="text-xs text-muted-foreground">마지막 계산 시각</p>
          {isLoading ? (
            <Skeleton className="h-7 w-32" />
          ) : (
            <p className="text-sm font-medium">
              {calculatedAt ? new Date(calculatedAt).toLocaleString('ko-KR') : '아직 없음'}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface CellSelection {
  cohortGroup: string;
  monthOffset: number;
}

function RetentionHeatmap({
  rows,
  maxMonthOffset,
  isLoading,
  selectedCell,
  onCellSelect,
}: {
  rows: Array<{
    cohortGroup: string;
    totalCustomers: number;
    cells: Array<{ monthOffset: number; repurchaseCount: number; retentionRate: number }>;
  }>;
  maxMonthOffset: number;
  isLoading: boolean;
  selectedCell: CellSelection | null;
  onCellSelect: (cell: CellSelection | null) => void;
}) {
  const offsets = Array.from({ length: maxMonthOffset + 1 }, (_, i) => i);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        title="코호트 데이터가 없습니다."
        description="동기화가 완료되면 코호트 데이터가 자동으로 생성돼요."
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse" data-testid={testIds.cohort.heatmapTable}>
        <thead>
          <tr>
            <th className="text-left px-3 py-2 font-medium text-muted-foreground whitespace-nowrap min-w-[140px]">
              코호트 그룹
            </th>
            <th className="px-3 py-2 font-medium text-muted-foreground whitespace-nowrap text-right min-w-[80px]">
              총 회원
            </th>
            {offsets.map((offset) => (
              <th
                key={offset}
                className="px-2 py-2 font-medium text-muted-foreground whitespace-nowrap text-center min-w-[56px]"
              >
                M+{offset}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const cellMap = new Map(row.cells.map((c) => [c.monthOffset, c]));
            return (
              <tr
                key={row.cohortGroup}
                className="border-t border-border"
                data-testid={testIds.cohort.heatmapRow}
              >
                <td className="px-3 py-2 font-medium whitespace-nowrap">{row.cohortGroup}</td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                  {row.totalCustomers.toLocaleString('ko-KR')}
                </td>
                {offsets.map((offset) => {
                  const cell = cellMap.get(offset);
                  const isSelected =
                    selectedCell?.cohortGroup === row.cohortGroup &&
                    selectedCell?.monthOffset === offset;

                  if (!cell) {
                    return (
                      <td key={offset} className="px-2 py-2 text-center">
                        <div className="rounded-lg px-1.5 py-1 bg-muted text-muted-foreground text-center">
                          —
                        </div>
                      </td>
                    );
                  }

                  const canSelect = offset > 0;
                  const cellContent = `${(cell.retentionRate * 100).toFixed(1)}%`;
                  const baseClassName = `rounded-lg px-1.5 py-1 tabular-nums font-medium text-center transition-all ${getHeatmapColor(cell.retentionRate)}`;

                  if (!canSelect) {
                    return (
                      <td key={offset} className="px-2 py-2 text-center">
                        <div className={baseClassName} title={`재구매: ${cell.repurchaseCount}명`}>
                          {cellContent}
                        </div>
                      </td>
                    );
                  }

                  return (
                    <td key={offset} className="px-2 py-2 text-center">
                      <button
                        type="button"
                        className={`${baseClassName} w-full cursor-pointer hover:ring-2 hover:ring-primary/30 ${isSelected ? 'ring-2 ring-primary shadow-sm' : ''}`}
                        title={`재구매: ${cell.repurchaseCount}명`}
                        onClick={() =>
                          onCellSelect(
                            isSelected
                              ? null
                              : { cohortGroup: row.cohortGroup, monthOffset: offset },
                          )
                        }
                      >
                        {cellContent}
                      </button>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CellActionDialog({
  cohortType,
  selectedCell,
  onClose,
}: {
  cohortType: CohortType;
  selectedCell: CellSelection;
  onClose: () => void;
}) {
  const [couponDialogOpen, setCouponDialogOpen] = useState(false);

  const {
    data: members,
    isLoading: isMembersLoading,
    isError: isMembersError,
  } = useCohortNonRepurchaseMembers({
    cohortType,
    cohortGroup: selectedCell.cohortGroup,
    monthOffset: selectedCell.monthOffset,
  });

  const memberCount = members?.count ?? 0;

  return (
    <>
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base">
              {selectedCell.cohortGroup} · M+{selectedCell.monthOffset}
            </DialogTitle>
            <DialogDescription>
              이 시점에 재구매하지 않은 고객에게 액션을 취하세요
            </DialogDescription>
          </DialogHeader>

          {isMembersError ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              데이터를 불러오지 못했어요
            </p>
          ) : (
            <>
              <div className="rounded-lg bg-muted/50 px-4 py-3">
                <div className="flex items-center gap-2 text-sm">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">미구매 고객</span>
                  {isMembersLoading ? (
                    <Skeleton className="h-4 w-10" />
                  ) : (
                    <span className="font-semibold tabular-nums">
                      {memberCount.toLocaleString('ko-KR')}명
                    </span>
                  )}
                </div>
              </div>

              <Button
                className="w-full gap-2 justify-center"
                disabled={memberCount === 0}
                onClick={() => setCouponDialogOpen(true)}
              >
                <Send className="h-4 w-4" />
                쿠폰 발급하기
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {memberCount > 0 && (
        <CouponIssueFromAnalysisDialog
          source="cohort"
          sourceMeta={{
            cohortType,
            cohortGroup: selectedCell.cohortGroup,
            monthOffset: selectedCell.monthOffset,
          }}
          cohortFilter={{
            cohortType,
            cohortGroup: selectedCell.cohortGroup,
            monthOffset: selectedCell.monthOffset,
          }}
          targetCount={memberCount}
          externalOpen={couponDialogOpen}
          onExternalOpenChange={(open) => {
            setCouponDialogOpen(open);
            if (!open) onClose();
          }}
        />
      )}
    </>
  );
}

function CohortPage() {
  const { mallId } = useParams({ from: '/_authenticated/$mallId/analysis/cohort/' });
  const [cohortType, setCohortType] = useState<CohortType>('first_purchase_month');
  const [selectedCell, setSelectedCell] = useState<CellSelection | null>(null);

  const { data: syncStatus, isLoading: isSyncLoading } = useSyncStatus(mallId);
  const {
    data: retentionData,
    isLoading: isRetentionLoading,
    isError: isRetentionError,
  } = useCohortRetention(cohortType);
  const { data: summaryData, isLoading: isSummaryLoading } = useCohortSummary(cohortType);

  const needsSync = !isSyncLoading && !syncStatus?.lastSyncAt;

  if (needsSync && !isRetentionLoading && !isSummaryLoading) {
    return (
      <AppLayout pageTitle="재구매 추이">
        <NeedsSyncState />
      </AppLayout>
    );
  }

  const totalCustomers = summaryData?.summaries.reduce((sum, s) => sum + s.totalCustomers, 0) ?? 0;
  const avgRetentionRate = summaryData?.avgRetentionRate ?? 0;
  const calculatedAt = summaryData?.calculatedAt ?? null;

  const handleCohortTypeChange = (type: CohortType) => {
    setCohortType(type);
    setSelectedCell(null);
  };

  return (
    <AppLayout pageTitle="재구매 추이">
      <div className="space-y-6 max-w-6xl mx-auto">
        <AnimatedContainer delay={0}>
          <PageHeader
            title="재구매 추이"
            description="어떤 시기에 유입된 고객이 재구매를 잘 하는지 확인합니다"
            data-testid={testIds.cohort.pageHeader}
          />
        </AnimatedContainer>

        <AnimatedContainer delay={0.05}>
          <MemberOnlyNotice />
        </AnimatedContainer>

        <AnimatedContainer delay={0.1}>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                {COHORT_TYPES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleCohortTypeChange(type)}
                    className={`px-3.5 py-1.5 rounded-xl text-xs font-medium transition-colors ${
                      cohortType === type
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                    data-testid={testIds.cohort.cohortTypeTab}
                  >
                    {COHORT_TYPE_LABELS[type]}
                  </button>
                ))}
              </div>
              {retentionData?.calculatedAt && (
                <span className="text-xs text-muted-foreground">
                  마지막 계산: {new Date(retentionData.calculatedAt).toLocaleString('ko-KR')}
                </span>
              )}
            </div>

            <AnalysisGuide title="이 분석은 이렇게 읽어요">
              <p className="text-xs text-muted-foreground/70">
                이 분석의 다른 이름은 <strong>코호트 분석</strong>이에요.
              </p>
              <p>
                <strong>{COHORT_TYPE_LABELS[cohortType]}</strong> 기준 —{' '}
                {COHORT_TYPE_DESCRIPTIONS[cohortType]}
              </p>
              <p>
                표의 <strong>가로줄</strong>은 고객 그룹, <strong>세로줄(M+0, M+1...)</strong>은 첫
                구매 후 몇 개월째인지를 뜻해요.
              </p>
              <p>
                각 칸의 숫자는 그 달에 다시 구매한 비율이에요. 색이 진할수록 재구매율이 높고,
                회색(—)은 아직 데이터가 없는 구간이에요.
              </p>
              <p className="text-xs text-muted-foreground/70">
                M+1 이후 셀을 클릭하면 해당 시점에 재구매하지 않은 고객에게 쿠폰을 보낼 수 있어요.
              </p>
              <div className="mt-3 pt-3 border-t border-border/50">
                <p className="font-medium text-foreground/80">이렇게 활용해 보세요</p>
                <ul className="mt-1.5 space-y-1 text-muted-foreground list-disc list-inside">
                  <li>
                    M+1, M+2에서 <strong>급격히 떨어지는 그룹</strong>이 있다면, 첫 구매 직후 리텐션
                    캠페인(감사 쿠폰, 후기 요청)을 보내보세요.
                  </li>
                  <li>
                    특정 코호트가 다른 그룹보다 <strong>꾸준히 높은 재구매율</strong>을 보인다면,
                    해당 유입 경로나 시기의 마케팅을 강화하는 게 효과적이에요.
                  </li>
                  <li>
                    전체적으로 M+3 이후 재구매율이 낮다면, 3개월 시점에 리마인더나 할인 오퍼를 자동
                    발송하는 것을 추천해요.
                  </li>
                </ul>
              </div>
            </AnalysisGuide>
          </div>
        </AnimatedContainer>

        <AnimatedContainer delay={0.2}>
          <SummaryCards
            avgRetentionRate={avgRetentionRate}
            totalCustomers={totalCustomers}
            calculatedAt={calculatedAt}
            isLoading={isSummaryLoading}
          />
        </AnimatedContainer>

        <AnimatedContainer delay={0.3}>
          <Card className="py-0 gap-0" data-testid={testIds.cohort.heatmapCard}>
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold">리텐션 히트맵</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  셀을 클릭하면 해당 시점의 미구매 고객에게 쿠폰을 발급할 수 있어요
                </p>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {!isRetentionLoading && isRetentionError ? (
                <DataErrorState />
              ) : (
                <RetentionHeatmap
                  rows={retentionData?.rows ?? []}
                  maxMonthOffset={retentionData?.maxMonthOffset ?? 0}
                  isLoading={isRetentionLoading}
                  selectedCell={selectedCell}
                  onCellSelect={setSelectedCell}
                />
              )}
              {selectedCell && (
                <CellActionDialog
                  cohortType={cohortType}
                  selectedCell={selectedCell}
                  onClose={() => setSelectedCell(null)}
                />
              )}
            </div>
          </Card>
        </AnimatedContainer>
      </div>
    </AppLayout>
  );
}
